import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { LayoutDashboard, IndianRupee, Users, User, LogOut, Menu, X, Link2, AlertTriangle, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { to: "/partner",             label: "Dashboard",      icon: LayoutDashboard, end: true, testId: "partner-nav-dashboard" },
  { to: "/partner/commissions", label: "My Earnings",    icon: IndianRupee,               testId: "partner-nav-commissions" },
  { to: "/partner/referrals",   label: "My Referrals",   icon: Users,                     testId: "partner-nav-referrals" },
  { to: "/partner/links",       label: "My Links",       icon: Link2,                     testId: "partner-nav-links" },
  { to: "/partner/kyc-status",  label: "KYC Status",     icon: ShieldCheck,               testId: "partner-nav-kyc-status" },
  { to: "/partner/profile",     label: "Profile & Bank", icon: User,                      testId: "partner-nav-profile" },
];

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const { refresh } = useAuth();
  const [form, setForm]     = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [show, setShow]     = useState({ current: false, next: false, confirm: false });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.next.length < 8)       { toast.error("New password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { withCredentials: true }
      );
      toast.success("Password changed! Please log in again with your new password.");
      onClose();
      // Backend clears the session cookie on password change (security best practice)
      // refresh() will see 401 → AuthContext redirects to login automatically
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setSaving(false); }
  };

  const inp = "w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary pr-10";
  const eye = "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground cursor-pointer";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-primary" />Change Password</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted text-muted-foreground">✕</button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          {[
            { key: "current", label: "Current (Temporary) Password" },
            { key: "next",    label: "New Password" },
            { key: "confirm", label: "Confirm New Password" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-muted-foreground mb-1.5">{label}</label>
              <div className="relative">
                <input
                  type={show[key] ? "text" : "password"}
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  className={inp}
                  required
                  data-testid={`change-pass-${key}`}
                />
                <button type="button" className={eye} onClick={() => setShow(s => ({ ...s, [key]: !s[key] }))}>
                  {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          <button type="submit" disabled={saving}
            data-testid="change-pass-submit"
            className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
            {saving ? "Changing…" : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function PartnerLayout() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [kycNotVerified, setKycNotVerified] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Fetch KYC status once on mount to show badge indicator
  useEffect(() => {
    axios.get(`${API}/api/sm/partners/portal/kyc/status`, { withCredentials: true })
      .then(r => {
        const s = r.data?.kyc_status;
        setKycNotVerified(!["verified", "active", "approved"].includes(s));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background partner-portal">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-screen w-[240px] bg-card border-r border-border flex flex-col z-40 transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        {/* Logo */}
        <div className="px-5 h-16 flex items-center justify-between border-b border-border">
          <Link to="/partner" className="flex items-center gap-2.5" data-testid="partner-logo">
            {branding.logo_src ? (
              <img src={branding.logo_src} alt={branding.app_name}
                style={{ height: 28, width: "auto", maxWidth: 80 }}
                className="object-contain"
                onError={e => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-7 h-7 border-2 border-primary rounded-md flex items-center justify-center shrink-0">
                <span className="font-serif text-primary text-sm font-bold">{(branding.app_name || "P")[0]}</span>
              </div>
            )}
            <div>
              <div className="font-serif text-[13px] leading-none text-foreground font-semibold">{branding.app_name || "InnoAim"}</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-primary mt-0.5">Partner Portal</div>
            </div>
          </Link>
          <button onClick={() => setDrawerOpen(false)} className="lg:hidden text-muted-foreground">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }>
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{item.label}</span>
              {item.to === "/partner/kyc-status" && kycNotVerified && (
                <span className="ml-auto w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" data-testid="kyc-status-badge" />
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info + Logout */}
        <div className="p-4 border-t border-border">
          <div className="px-2 py-2 mb-2">
            <div className="text-sm text-foreground truncate font-medium">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/partner/login"); }}
            data-testid="partner-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b border-border flex items-center px-4 z-30">
        <button onClick={() => setDrawerOpen(true)} className="text-muted-foreground mr-3">
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <span className="font-serif text-primary text-sm">Partner Portal</span>
      </div>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Main content */}
      <main className="lg:pl-[240px] min-h-screen">
        <div className="pt-14 lg:pt-0">
          {/* Temporary password banner */}
          {user?.must_reset_password && (
            <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3"
              data-testid="must-reset-password-banner">
              <div className="flex items-center gap-2.5 min-w-0">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <p className="text-[13px] text-amber-700 dark:text-amber-400 truncate">
                  You're using a temporary password. Please change it now to secure your account.
                </p>
              </div>
              <button
                onClick={() => setShowChangePw(true)}
                data-testid="change-password-banner-btn"
                className="text-[12px] font-semibold text-amber-600 border border-amber-500/40 px-3 py-1 rounded-full hover:bg-amber-500/10 transition-colors shrink-0">
                Change Password
              </button>
            </div>
          )}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Password change modal */}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
